import { z } from "zod";
import { TransactionType } from "@common/types/transaction";
import { NativeInvoiceTermType } from "@common/helpers/invoice-terms.helper";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import { zQueryBoolean } from "@common/schemas/query-boolean.schema";
import { PG_INT4_MAX } from "@common/schemas/shared.schema";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { ClassFilterType } from "@common/types/class";
import { DocumentRetrievalExclusionReason } from "@common/types/documentRetrieval";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import {
    SchedulesReportMethod,
    SchedulesReportLayout,
    SchedulesReportRevenueSubMode,
    SchedulesReportRows,
} from "@common/types/reports/schedules-report";
import {
    accountSubtypeSchema,
    form1099BoxFilterValueSchema,
} from "@common/schemas/domains/accounting/account.schema";
import {
    generalLedgerFilterRoleSchema,
    generalLedgerFilterTypeSchema,
} from "@common/helpers/accounting/general-ledger-filter";
import { DocumentFlowStatusFiltersSchema } from "@common/schemas/financialDocument/document-flow-status-filters.schema";
import { InvoiceStatusFilterSchema } from "@common/schemas/domains/invoice/invoice-status-filter.schema";
import { SourcePredicateSchema } from "@common/contracts/auditLog.contract";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import {
    MoneyDirection,
    TransactionLocked,
    AccountingReportCycle,
    AccountingReportCycleTotal,
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportRatioType,
} from "../../constants";
import { OrganizationServicesOffered } from "../organization";
import { OrganizationEntityPriority } from "../organizationEntity";
import { TransactionCoaFilterType } from "../../helpers/transactions";
import { BillStatus } from "../bill";
import { BillStatusFilterSchema } from "@common/schemas/domains/bills/bill-status-filter.schema";
import {
    AccountRolePresence,
    AccountRulePresence,
    AccountSource,
    AccountStatus,
} from "../domains/accounting/account";
import { InvoiceSentStatus, InvoiceSource, InvoiceStatus } from "../invoice";
import {
    ENGAGEMENT_TEAM_ROLES,
    TaskTemplateAssigneeRole,
} from "../taskTemplateAssigneeRole";
import { DocumentMatchStatus } from "../matchStatus";
import {
    AccrualDraftCounterpartyState,
    AccrualReviewStatus,
} from "../financialDocument";
import { AccrualSuggestionBucket } from "../accrualSuggestionBucket";
import { TransactionMatchType } from "../transactionMatchType";
import { TransactionStatus } from "../transaction";
import { CounterpartySource } from "../counterparty";
import {
    EntityCloseStage,
    EntityCloseStatus,
    EntityProfileIndustry,
    EntityProfileType,
} from "../entity";
import { AccountingLedgerBasis } from "../domains/accounting/ledger";
import {
    AmountRoundingLevel,
    RuleActionType,
    RuleReviewStatus,
    RuleSourceFilter,
    RuleStatus,
} from "../transactionRule";
import {
    AmountFilterLevel,
    CounterpartyDisabledFilter,
} from "../../constants/counterparty";
import { ActorType } from "../auditLog";
import { CloseTemplateBasis } from "../closeTemplateBasis";
import { CloseApplicationType } from "../closeApplicationType";
import { CloseHealth } from "../closeHealth";
import { CloseProgressStatus } from "../closeProgress";
import { CloseStatus } from "../closeStatus";
import { WORK_DELIVERY_DATE_PRESETS } from "../workDeliveryDatePreset";
import { ClientsWorkOverviewSortValue } from "../views/clientsWorkOverviewSort";
import { TransactionReviewField } from "../transactionFieldReview";
import { genericCounterpartyReviewFieldEnum } from "@common/schemas/counterpartyFieldReview.schema";
import { CatalogItemFilterType } from "../catalogItem";
import { ServiceTemplateRecurrencePeriod } from "../serviceTemplateRecurrencePeriod";
import { WhoPaysOption } from "./clients";

export { zQueryBoolean };

/**
 * Calendar-day bounds (YYYY-MM-DD). Timezone-free alternative to `start`/`end`
 * for saved views, where an anchored timestamp shifts the day per viewer.
 */
export const calendarDateFiltersSchema = z.object({
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
});
export type CalendarDateFilters = z.infer<typeof calendarDateFiltersSchema>;

export const filtersSchema = z
    .object({
        search: z.string().optional(),
        start: z.coerce.date().optional(),
        end: z.coerce.date().optional(),
        minAmount: z.coerce.number().optional(),
        maxAmount: z.coerce.number().optional(),
        direction: z.nativeEnum(MoneyDirection).optional(),
        amountRounded: zQueryBoolean().optional(),
        amountRoundingLevel: z.nativeEnum(AmountRoundingLevel).optional(),
        category: z.array(z.string()).optional(),
        requiredAction: zQueryBoolean().optional(),
        requiredActions: z.array(z.string()).optional(),
        hasTransferMatch: zQueryBoolean().optional(),
        transactionRulesIds: z.array(z.string().uuid()).optional(),
        statuses: z.array(z.nativeEnum(TransactionStatus)).optional(),
        classes: z.array(z.string().uuid()).optional(),
        counterpartiesIds: z.array(z.string().uuid()).optional(),
        withEmptyOrUnverifiedCounterparty: zQueryBoolean().optional(),
        hasCounterparty: zQueryBoolean().optional(),
        customerIds: z.array(z.string().uuid()).optional(),
        withEmptyCustomer: zQueryBoolean().optional(),
        hasCustomer: zQueryBoolean().optional(),
        // Filters transactions by the status of the native invoice their
        // billable expense was billed on (transaction.billableExpenseInvoiceId).
        // Distinct from `invoiceStatuses`, which filters invoices directly on
        // the Invoices page.
        billableExpenseInvoiceStatuses: z
            .array(z.nativeEnum(InvoiceStatus))
            .optional(),
        // Counterparty directory "created by" filter (Kick, specific members,
        // or integration sources such as Stripe/Ramp/Mercury/QuickBooks)
        createdByKick: zQueryBoolean().optional(),
        createdByUserIds: z.array(z.coerce.number().int()).optional(),
        createdBySources: z.array(z.nativeEnum(CounterpartySource)).optional(),
        matchTypes: z.array(z.nativeEnum(TransactionMatchType)).optional(),
        isSplitted: zQueryBoolean().optional(),
        isNotSplitted: zQueryBoolean().optional(),
        isReconciled: zQueryBoolean().optional(),
        isNotReconciled: zQueryBoolean().optional(),
        isUnreconciledCheck: zQueryBoolean().optional(),
        transactionTypes: z.array(z.nativeEnum(TransactionType)).optional(),
        hasReceipt: zQueryBoolean().optional(),
        noReceipt: zQueryBoolean().optional(),
        requiresReceipt: zQueryBoolean().optional(),
        hasNote: zQueryBoolean().optional(),
        noNote: zQueryBoolean().optional(),
        requiresNote: zQueryBoolean().optional(),
        entityIds: z
            .array(z.coerce.number())
            .optional()
            .transform((val) => (val?.length ? val : undefined)),
        accountIds: z.array(z.coerce.number()).optional(),
        hasNoFinancialAccount: zQueryBoolean().optional(),
        subaccountIds: z.array(z.string().uuid()).optional(),
        // Invoices-page Account filter: selects invoices by source (currently
        // only Kick/MANUAL, which has no financialAccountId) alongside
        // `accountIds`, so the Kick pseudo-account can be combined with real
        // connected accounts in one Account filter selection.
        invoiceSources: z.array(z.nativeEnum(InvoiceSource)).optional(),
        classFilterType: z.nativeEnum(ClassFilterType).optional(),
        ...DocumentFlowStatusFiltersSchema.shape,
        periodStart: z.string().date().optional(),
        periodEnd: z.string().date().optional(),
        excludedPeriods: z
            .record(z.nativeEnum(DocumentRetrievalExclusionReason))
            .optional(),
        documentCategoryIds: z.array(z.string().uuid()).optional(),
        entityId: z.coerce.number().optional(),
        accountCodes: z.array(z.coerce.number()).optional(),
        accountStatus: z.nativeEnum(AccountStatus).optional(),
        transactionLocked: z.nativeEnum(TransactionLocked).optional(),
        accountSource: z.nativeEnum(AccountSource).optional(),
        accountRolePresence: z.nativeEnum(AccountRolePresence).optional(),
        accountRulePresence: z.nativeEnum(AccountRulePresence).optional(),
        coaTemplateGroupKeys: z.array(z.string()).optional(),
        accountSubtypes: z.array(accountSubtypeSchema).optional(),
        form1099Boxes: z.array(form1099BoxFilterValueSchema).optional(),
        cashGeneralLedgerRoles: z
            .array(generalLedgerFilterRoleSchema)
            .optional(),
        cashGeneralLedgerTypes: z
            .array(generalLedgerFilterTypeSchema)
            .optional(),
        accrualGeneralLedgerRoles: z
            .array(generalLedgerFilterRoleSchema)
            .optional(),
        accrualGeneralLedgerTypes: z
            .array(generalLedgerFilterTypeSchema)
            .optional(),
        cycle: z
            .union([
                z.nativeEnum(AccountingReportCycle),
                z.nativeEnum(AccountingReportCycleTotal),
            ])
            .optional(),
        comparisons: z
            .array(z.nativeEnum(AccountingReportComparison))
            .optional(),
        comparisonDiffs: z
            .array(z.nativeEnum(AccountingReportComparisonDiff))
            .optional(),
        ratioTypes: z.array(z.nativeEnum(AccountingReportRatioType)).optional(),
        consolidationGroupId: z.string().uuid().optional(),
        invoiceStatuses: z.array(z.nativeEnum(InvoiceStatus)).optional(),
        externalInvoiceStatuses: z
            .array(z.nativeEnum(InvoiceStatus))
            .optional(),
        invoiceStatusFilter: InvoiceStatusFilterSchema.optional(),
        catalogItemIds: z.array(z.string().uuid()).optional(),
        catalogItemFilterType: z.nativeEnum(CatalogItemFilterType).optional(),
        sourceTemplateIds: z.array(z.string().uuid()).optional(),
        incomeAccountIds: z.array(z.string().uuid()).optional(),
        receivablesAccountIds: z.array(z.string().uuid()).optional(),
        isSubscription: zQueryBoolean().optional(),
        isEligible1099: zQueryBoolean().optional(),
        disabledFilter: z.nativeEnum(CounterpartyDisabledFilter).optional(),
        shouldExcludeCreditCardJournalEntries: zQueryBoolean().optional(),
        shouldExcludePaymentProcessorJournalEntries: zQueryBoolean().optional(),
        includeExcludedAmounts: zQueryBoolean().optional(),
        excludeFrom1099: zQueryBoolean().optional(),
        billStatuses: z.array(z.nativeEnum(BillStatus)).optional(),
        externalBillStatuses: z.array(z.nativeEnum(BillStatus)).optional(),
        billStatusFilter: BillStatusFilterSchema.optional(),
        matchStatuses: z.array(z.nativeEnum(DocumentMatchStatus)).optional(),
        minOverdue: z.coerce.number().optional(),
        maxOverdue: z.coerce.number().optional(),
        minDue: z.coerce.number().optional(),
        maxDue: z.coerce.number().optional(),
        overdue: zQueryBoolean().optional(),
        ruleStatuses: z.array(z.nativeEnum(RuleStatus)).optional(),
        ruleSources: z.array(z.nativeEnum(RuleSourceFilter)).optional(),
        ruleActionTypes: z.array(z.nativeEnum(RuleActionType)).optional(),
        coaAccounts: z.array(z.string().uuid()).optional(),
        splitCoaAccounts: z.array(z.string().uuid()).optional(),
        coaFilterType: z
            .array(z.nativeEnum(TransactionCoaFilterType))
            .optional(),
        accrualCoaAccounts: z.array(z.string().uuid()).optional(),
        includeUnassignedAccrualAccount: zQueryBoolean().optional(),
        transactionIds: z.array(z.coerce.number()).max(5000).optional(),
        billIds: z.array(z.string()).optional(),
        invoiceIds: z.array(z.string()).optional(),
        excludeSplits: zQueryBoolean().optional(),
        includeBeforeBookkeepingStart: zQueryBoolean().optional(),
        excludedTransactionIds: z.array(z.coerce.number()).optional(),
        normalizedDescriptionId: z.string().optional(),
        normalizedDescriptionIds: z.array(z.string().uuid()).max(50).optional(),
        accrualAccountId: z.string().uuid().nullable().optional(),
        isAccrualReviewed: zQueryBoolean().optional(),
        isNotAccrualReviewed: zQueryBoolean().optional(),
        /** No invoice or bill match, and either no accrual account or a live schedule: what accrual approve still accepts. */
        isNotAccrualApproved: zQueryBoolean().optional(),
        isScheduleSuspicious: zQueryBoolean().optional(),
        hasScheduleDraft: zQueryBoolean().optional(),
        /** Skips transactions a live recognition schedule keeps in their entity. */
        excludeLiveScheduled: zQueryBoolean().optional(),
        accrualSuggestionBuckets: z
            .array(z.nativeEnum(AccrualSuggestionBucket))
            .optional(),
        journalEntrySources: z
            .array(z.nativeEnum(JournalEntrySourceFilter))
            .optional(),
        // Schedules report views persist the full report parameter set.
        ledgerId: z.string().uuid().optional(),
        scheduleLayout: z.nativeEnum(SchedulesReportLayout).optional(),
        scheduleRows: z.nativeEnum(SchedulesReportRows).optional(),
        revenueSubMode: z.nativeEnum(SchedulesReportRevenueSubMode).optional(),
        scheduleMethod: z.nativeEnum(SchedulesReportMethod).optional(),
        datePreset: z.string().optional(),
        holdingAccountIds: z.array(z.string().uuid()).optional(),
        scheduleGroups: z.array(z.string()).optional(),
        includeUnassignedCounterparty: zQueryBoolean().optional(),
        termStartFrom: z.string().date().optional(),
        termStartTo: z.string().date().optional(),
        termEndFrom: z.string().date().optional(),
        termEndTo: z.string().date().optional(),
        termTypes: z.array(z.nativeEnum(NativeInvoiceTermType)).optional(),
        sentStatuses: z.array(z.nativeEnum(InvoiceSentStatus)).optional(),
        sentByUserIds: z
            .array(z.coerce.number().int().min(1).max(PG_INT4_MAX))
            .optional(),
        // A/R Aging Detail: aging-period filter (replaces the old standalone
        // Bucket dropdown), scoping the report to these buckets' sections.
        agingBuckets: z.array(z.nativeEnum(ArAgingBucket)).optional(),
        createdOnFrom: z.string().date().optional(),
        createdOnTo: z.string().date().optional(),
        dueDateFrom: z.string().date().optional(),
        dueDateTo: z.string().date().optional(),
        invoiceDateFrom: z.string().date().optional(),
        invoiceDateTo: z.string().date().optional(),
        accrualReviewStatuses: z
            .array(z.nativeEnum(AccrualReviewStatus))
            .optional(),
        draftSources: z.array(z.string()).optional(),
        draftCounterpartyStates: z
            .array(z.nativeEnum(AccrualDraftCounterpartyState))
            .optional(),
        hasDueDate: zQueryBoolean().optional(),
        amountFilterLevel: z.nativeEnum(AmountFilterLevel).optional(),

        // Clients Page filters
        bookkeepingStartDateStart: z.coerce.date().optional(),
        bookkeepingStartDateEnd: z.coerce.date().optional(),
        closeStatus: z.array(z.nativeEnum(EntityCloseStatus)).optional(),
        closeStage: z.array(z.nativeEnum(EntityCloseStage)).optional(),
        closeDateStart: z.coerce.date().optional(),
        closeDateEnd: z.coerce.date().optional(),
        dueDateStart: z.coerce.date().optional(),
        dueDateEnd: z.coerce.date().optional(),
        teamMemberIds: z.array(z.coerce.number()).optional(),
        legalTypes: z.array(z.nativeEnum(EntityProfileType)).optional(),
        entityIndustries: z
            .array(z.nativeEnum(EntityProfileIndustry))
            .optional(),
        entityBases: z.array(z.nativeEnum(AccountingLedgerBasis)).optional(),
        assigneeIds: z.array(z.coerce.number()).optional(),
        plans: z.array(z.nativeEnum(FLAT_RATE_PLANS)).optional(),
        whoPays: z.array(z.nativeEnum(WhoPaysOption)).optional(),
        services: z.array(z.nativeEnum(OrganizationServicesOffered)).optional(),
        priority: z.array(z.nativeEnum(OrganizationEntityPriority)).optional(),
        accountClasses: z.array(z.nativeEnum(AccountClass)).optional(),
        hasUnread: zQueryBoolean().optional(),

        // History Page filters
        actorIds: z
            .array(z.union([z.literal(null), z.coerce.number()]))
            .optional(),
        actorType: z.nativeEnum(ActorType).optional(),
        sourceTypes: z.array(z.string()).optional(),
        sourcePredicates: z.array(SourcePredicateSchema).optional(),
        processSourceTypes: z.array(z.string()).optional(),
        processFields: z.array(z.string()).optional(),
        fields: z.array(z.string()).optional(),

        // Financial Documents Page filters
        uploaderIds: z
            .array(z.union([z.literal(null), z.coerce.number().int()]))
            .optional(),
        uploaderType: z.nativeEnum(ActorType).optional(),
        uploaderSources: z.array(z.string()).optional(),
        fileExtensions: z.array(z.string()).optional(),
        // Financial documents task membership: pins the documents a task shows
        financialDocumentIds: z.array(z.coerce.number().int()).optional(),
        hasNoEntity: zQueryBoolean().optional(),
        hasNoCounterparty: zQueryBoolean().optional(),

        // Audit log filter (for viewing transactions from history)
        auditLogFilter: z
            .object({
                date: z.coerce.date(),
                sourceType: z.string(),
                sourceIds: z.array(z.string()).optional(),
                actorId: z.coerce.number().nullable().optional(),
                changeTypes: z.array(z.string()).optional(),
                fields: z.array(z.string()).optional(),
            })
            .optional(),

        // Reconciliation task filter — stores selected financial account types
        financialAccountTypes: z.array(z.string()).optional(),

        // Tasks Page filters
        taskAssigneeIds: z.array(z.coerce.number()).optional(),
        isTaskUnassigned: zQueryBoolean().optional(),
        isTaskAssignedToClient: zQueryBoolean().optional(),
        taskCreatedByIds: z.array(z.coerce.number()).optional(),
        isTaskCreatedByClient: zQueryBoolean().optional(),
        taskClientIds: z.array(z.string().uuid()).optional(),
        taskCreatedAtStart: z.coerce.date().optional(),
        taskCreatedAtEnd: z.coerce.date().optional(),
        taskCompletedByIds: z.array(z.coerce.number()).optional(),
        taskCompletedAtStart: z.coerce.date().optional(),
        taskCompletedAtEnd: z.coerce.date().optional(),
        taskCloseIds: z.array(z.string().uuid()).optional(),
        // Cannot use z.nativeEnum(TaskPriority) here due to the same
        // circular dependency noted above for taskStatuses/taskSort.
        taskPriorities: z
            .array(z.enum(["urgent", "high", "medium", "low"]))
            .optional(),
        hasAttachments: zQueryBoolean().optional(),
        taskGroupIds: z.array(z.string().uuid()).optional(),
        hasBlockedBy: zQueryBoolean().optional(),
        hasBlocking: zQueryBoolean().optional(),
        hasRelated: zQueryBoolean().optional(),
        hasUnreadChat: zQueryBoolean().optional(),
        taskCloseStageIds: z.array(z.string().uuid()).optional(),
        // Cannot use z.nativeEnum(TaskStatus) / z.nativeEnum(TaskSortValue) here
        // due to circular dependency: general.ts → task.ts → views/views.ts → general.ts
        taskStatuses: z
            .array(
                z.enum(["todo", "in_progress", "blocked", "done", "cancelled"]),
            )
            .optional(),
        taskSort: z
            .enum([
                "NAME_ASC",
                "NAME_DESC",
                "DUE_DATE_ASC",
                "DUE_DATE_DESC",
                "ESTIMATE_ASC",
                "ESTIMATE_DESC",
                "PRIORITY_ASC",
                "PRIORITY_DESC",
                "STATUS_ASC",
                "STATUS_DESC",
                "CREATED_AT_ASC",
                "CREATED_AT_DESC",
                "CREATED_BY_ASC",
                "CREATED_BY_DESC",
                "ASSIGNEE_ASC",
                "ASSIGNEE_DESC",
                "UPDATED_AT_ASC",
                "UPDATED_AT_DESC",
                "COMPLETED_AT_ASC",
                "COMPLETED_AT_DESC",
                "COMPLETED_BY_ASC",
                "COMPLETED_BY_DESC",
                "CLIENT_ASC",
                "CLIENT_DESC",
            ])
            .optional(),

        // Org task templates page filters. Types stay plain strings because
        // importing TaskType here would recreate the cycle noted above.
        orgTaskTemplateTypes: z.array(z.string()).optional(),
        orgTaskTemplateGroupIds: z.array(z.string().uuid()).optional(),
        orgTaskTemplateAssigneeRoles: z
            .array(z.nativeEnum(TaskTemplateAssigneeRole))
            .optional(),
        orgTaskTemplateCreatedByUserIds: z
            .array(z.coerce.number().int())
            .optional(),

        // Templates Page filters
        closeTemplateBases: z
            .array(z.nativeEnum(CloseTemplateBasis))
            .optional(),
        closeTemplateUpdatedByUserIds: z.array(z.coerce.number()).optional(),
        closeTemplateOwnerIds: z.array(z.coerce.number()).optional(),

        // Invoice Templates Page filter: creator (workspace member) multi-select
        invoiceTemplateCreatedByUserIds: z
            .array(z.coerce.number().int())
            .optional(),
        closeTemplateType: z.enum(["general", "client_specific"]).optional(),
        // URL-scoped filter when arriving via schedules "Go to template"
        templateIds: z.array(z.string().uuid()).optional(),

        // Work Page org-specific filters (UUID-keyed, distinct from enum-based closeStage/services)
        closeTemplateIds: z.array(z.string().uuid()).optional(),
        closeStageIds: z.array(z.string().uuid()).optional(),
        closeStatuses: z.array(z.nativeEnum(CloseStatus)).optional(),
        orgServiceIds: z.array(z.string().uuid()).optional(),
        orgServiceNoTier: zQueryBoolean().optional(),
        applicationTypes: z
            .array(z.nativeEnum(CloseApplicationType))
            .optional(),
        progressStatuses: z.array(z.nativeEnum(CloseProgressStatus)).optional(),
        healthStatuses: z.array(z.nativeEnum(CloseHealth)).optional(),
        // Work Page sort, saved with the view rather than sent as a filter
        workSort: z.nativeEnum(ClientsWorkOverviewSortValue).optional(),

        // Work Page date filters
        workStartDateStart: z.coerce.date().optional(),
        workStartDateEnd: z.coerce.date().optional(),
        workDeliveryDateStart: z.coerce.date().optional(),
        workDeliveryDateEnd: z.coerce.date().optional(),
        // Wins over the Work end dates; the page resolves it before sending
        workDeliveryDatePreset: z.enum(WORK_DELIVERY_DATE_PRESETS).optional(),

        // Service template schedule occurrences drawer date filter
        applicationDateStart: z.coerce.date().optional(),
        applicationDateEnd: z.coerce.date().optional(),

        // CoA-template applied-entities drawer date filters
        coaLastUpdatedStart: z.coerce.date().optional(),
        coaLastUpdatedEnd: z.coerce.date().optional(),
        coaAppliedAtStart: z.coerce.date().optional(),
        coaAppliedAtEnd: z.coerce.date().optional(),

        // Work Page numeric filters
        daysInStageOperator: z.enum(["gt", "lt", "eq", "between"]).optional(),
        daysInStageMin: z.coerce.number().int().nonnegative().optional(),
        daysInStageMax: z.coerce.number().int().nonnegative().optional(),

        // Review filter
        reviewedFields: z
            .array(z.nativeEnum(TransactionReviewField))
            .optional(),
        reviewedFieldsMode: z.enum(["any", "every"]).optional(),
        unreviewedFields: z
            .array(z.nativeEnum(TransactionReviewField))
            .optional(),
        unreviewedFieldsMode: z.enum(["any", "every"]).optional(),
        reviewerIds: z.array(z.coerce.number()).optional(),
        reviewerMode: z.enum(["all", "any", "none"]).optional(),

        // Rules page review filter
        ruleReviewStatuses: z.array(z.nativeEnum(RuleReviewStatus)).optional(),
        ruleReviewerIds: z.array(z.coerce.number()).optional(),
        ruleReviewerMode: z.enum(["all", "any", "none"]).optional(),

        // Counterparty directory review filter
        counterpartyReviewedFields: z
            .array(genericCounterpartyReviewFieldEnum)
            .optional(),
        counterpartyReviewedFieldsMode: z.enum(["any", "every"]).optional(),
        counterpartyUnreviewedFields: z
            .array(genericCounterpartyReviewFieldEnum)
            .optional(),
        counterpartyUnreviewedFieldsMode: z.enum(["any", "every"]).optional(),
        counterpartyReviewerIds: z.array(z.coerce.number()).optional(),
        counterpartyReviewerMode: z.enum(["all", "any", "none"]).optional(),

        // Engagement team filter
        engagementTeamRoles: z.array(z.enum(ENGAGEMENT_TEAM_ROLES)).optional(),
        engagementTeamRoleOperator: z.enum(["any", "every"]).optional(),
        engagementTeamMemberIds: z.array(z.coerce.number().int()).optional(),
        engagementTeamMemberOperator: z.enum(["any", "every"]).optional(),

        // Service template schedules page filter (recurrence period, e.g. daily/weekly)
        schedulePeriods: z
            .array(z.nativeEnum(ServiceTemplateRecurrencePeriod))
            .optional(),
        // Service template schedules page filter: Service (i.e. close
        // template) multi-select, distinct from `templateIds` (the
        // single-template URL scope used by the Templates page's
        // "Go to template" action).
        scheduleTemplateIds: z.array(z.string().uuid()).optional(),
        // Service template schedules page filter: Client (i.e. workspace)
        // multi-select, matched against the schedule's workspaceId.
        scheduleClientIds: z.array(z.string().uuid()).optional(),

        // Work Page filter: Client (i.e. workspace) multi-select, matched
        // against the close's workspaceId. Distinct from `scheduleClientIds`
        // (Schedules page) despite the identical shape.
        workClientIds: z.array(z.string().uuid()).optional(),
    })
    .strict();

export type FiltersTypes = z.infer<typeof filtersSchema>;
